import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { getRecentActivity } from '../../../services/dashboardService';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { formatCurrency } from '../../../utils/currency';

const RecentActivity = () => {
  const navigate = useNavigate();
  const { data: currentCompany } = useCurrentCompany();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentCompany) {
      loadRecentActivity();
    }
  }, [currentCompany]);

  const formatAmountWithCurrency = (amount) => {
    if (!currentCompany?.currency) {
      return formatCurrency(amount);
    }
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currentCompany.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (error) {
      console.warn('Invalid currency code:', currentCompany.currency, 'falling back to formatCurrency');
      return formatCurrency(amount);
    }
  };

  const loadRecentActivity = async () => {
    setLoading(true);
    try {
      const data = await getRecentActivity(currentCompany?.id);
      
      // Transform activities to include formatted amounts and proper display data
      const transformedActivities = data.map(activity => ({
        id: activity.id,
        title: getActivityTitle(activity),
        description: getActivityDescription(activity),
        timestamp: formatTimestamp(activity.date),
        icon: getActivityIcon(activity.type),
        iconColor: getActivityIconColor(activity.type),
        path: getActivityPath(activity),
        amount: activity.amount ? formatAmountWithCurrency(activity.amount) : null
      }));
      
      setActivities(transformedActivities);
    } catch (error) {
      console.error('Error loading recent activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityTitle = (activity) => {
    switch (activity.type) {
      case 'invoice':
        return 'Invoice Created';
      case 'expense':
        return 'Expense Recorded';
      case 'payment':
        return 'Payment Received';
      default:
        return 'Activity';
    }
  };

  const getActivityDescription = (activity) => {
    if (activity.amount) {
      return `${activity.description} - ${formatAmountWithCurrency(activity.amount)}`;
    }
    return activity.description;
  };

  const formatTimestamp = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
      
      if (diffInHours < 1) {
        return 'Just now';
      } else if (diffInHours < 24) {
        return `${diffInHours}h ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
      }
    } catch (error) {
      return '';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'invoice':
        return 'FileText';
      case 'expense':
        return 'Receipt';
      case 'payment':
        return 'DollarSign';
      default:
        return 'Activity';
    }
  };

  const getActivityIconColor = (type) => {
    switch (type) {
      case 'invoice':
        return 'primary';
      case 'expense':
        return 'secondary';
      case 'payment':
        return 'success';
      default:
        return 'accent';
    }
  };

  const getActivityPath = (activity) => {
    switch (activity.type) {
      case 'invoice':
        return '/invoices';
      case 'expense':
        return '/expenses';
      case 'payment':
        return '/financial-reports';
      default:
        return '/dashboard';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground">
          Recent Activity
        </h2>
        <button 
          onClick={() => navigate('/financial-reports')}
          className="caption text-primary hover:text-primary/80 transition-smooth"
        >
          View All
        </button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="mt-2 text-sm text-muted-foreground">Loading activity...</p>
          </div>
        </div>
      ) : activities?.length > 0 ? (
        <div className="space-y-3">
          {activities?.map((activity) => (
            <button
              key={activity?.id}
              onClick={() => navigate(activity?.path)}
              className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-muted transition-smooth hover-lift active-press text-left"
            >
              <div className={`
                w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0
                ${activity?.iconColor === 'primary' ? 'bg-primary/10' : ''}
                ${activity?.iconColor === 'secondary' ? 'bg-secondary/10' : ''}
                ${activity?.iconColor === 'accent' ? 'bg-accent/10' : ''}
                ${activity?.iconColor === 'success' ? 'bg-success/10' : ''}
              `}>
                <Icon name={activity?.icon} size={18} color={`var(--color-${activity?.iconColor})`} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-body font-medium text-sm text-foreground mb-1">
                  {activity?.title}
                </h3>
                <p className="caption text-muted-foreground line-clamp-1 mb-1">
                  {activity?.description}
                </p>
                <p className="caption text-muted-foreground">
                  {activity?.timestamp}
                </p>
              </div>
              <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="Activity" size={32} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No recent activity</p>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;